import type { DocumentConvertInput, DocumentPage, DocumentSection } from "@gencow/core";

type LocalDocumentExtractionInput = {
  filename: string;
  mimeType: string;
  buffer: Buffer;
};

type LocalDocumentExtractionResult = {
  text: string;
  markdown: string;
  pages: DocumentPage[];
  sections: DocumentSection[];
  warnings: string[];
};

type MammothMarkdownConverter = {
  convertToMarkdown(input: { buffer: Buffer }): Promise<{ value?: string }>;
};

function normalizeNewlines(value: string): string {
  return value.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}

function normalizeText(value: string): string {
  return normalizeNewlines(value)
    .replace(/[ \t]+\n/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\s+([,.;:!?])/g, "$1")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function decodeHtmlEntities(value: string): string {
  return value
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
}

function htmlToMarkdown(html: string): string {
  let output = html;

  output = output.replace(/<script[\s\S]*?<\/script>/gi, "");
  output = output.replace(/<style[\s\S]*?<\/style>/gi, "");
  output = output.replace(/<h([1-6])[^>]*>([\s\S]*?)<\/h\1>/gi, (_match, level, content) => {
    const depth = Number(level);
    return `${"#".repeat(depth)} ${decodeHtmlEntities(content.replace(/<[^>]+>/g, " ").trim())}\n\n`;
  });
  output = output.replace(/<(br|hr)\s*\/?>/gi, "\n");
  output = output.replace(/<\/(p|div|section|article|li|ul|ol|table|tr)>/gi, "\n");
  output = output.replace(/<(p|div|section|article|li|ul|ol|table|tr|td|th)[^>]*>/gi, " ");
  output = output.replace(/<[^>]+>/g, " ");

  return normalizeText(decodeHtmlEntities(output));
}

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (inQuotes) {
      if (char === '"') {
        if (text[index + 1] === '"') {
          field += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(field.trim());
      field = "";
    } else if (char === "\n" || (char === "\r" && text[index + 1] === "\n")) {
      row.push(field.trim());
      if (row.some((value) => value.length > 0)) {
        rows.push(row);
      }
      row = [];
      field = "";
      if (char === "\r") {
        index += 1;
      }
    } else {
      field += char;
    }
  }

  row.push(field.trim());
  if (row.some((value) => value.length > 0)) {
    rows.push(row);
  }

  return rows;
}

function csvToMarkdown(text: string): { markdown: string; plainText: string } {
  const rows = parseCsv(text);
  if (rows.length === 0) {
    return { markdown: "", plainText: "" };
  }

  if (rows.length === 1) {
    const single = rows[0].join(", ");
    return { markdown: single, plainText: single };
  }

  const header = rows[0];
  const body = rows.slice(1);
  const table = [
    `| ${header.join(" | ")} |`,
    `| ${header.map(() => "---").join(" | ")} |`,
    ...body.map((row) => `| ${header.map((_, index) => row[index] ?? "").join(" | ")} |`),
  ].join("\n");

  const plainText = body
    .map((row) => header.map((title, index) => `${title}: ${row[index] ?? ""}`).join(", "))
    .join("\n");
  return { markdown: table, plainText };
}

function buildPages(text: string): DocumentPage[] {
  return [
    {
      page: 1,
      charStart: 0,
      charEnd: text.length,
      previewText: text.slice(0, 160),
    },
  ];
}

function buildFallbackSection(markdown: string, title?: string): DocumentSection[] {
  return [
    {
      id: "section-1",
      title,
      depth: 1,
      path: title ? [title] : [],
      pageStart: 1,
      pageEnd: 1,
      charStart: 0,
      charEnd: markdown.length,
    },
  ];
}

function deriveSections(markdown: string): DocumentSection[] {
  const headingRegex = /^(#{1,6})\s+(.+)$/gm;
  const matches = Array.from(markdown.matchAll(headingRegex));
  if (matches.length === 0) {
    return buildFallbackSection(markdown);
  }

  const sections: DocumentSection[] = [];
  const pathStack: string[] = [];

  for (let index = 0; index < matches.length; index += 1) {
    const match = matches[index];
    const depth = match[1].length;
    const title = match[2].trim();
    const charStart = match.index ?? 0;
    const charEnd =
      index + 1 < matches.length ? (matches[index + 1].index ?? markdown.length) : markdown.length;

    pathStack.splice(depth - 1);
    pathStack[depth - 1] = title;

    sections.push({
      id: `section-${index + 1}`,
      title,
      depth,
      path: pathStack.filter(Boolean),
      pageStart: 1,
      pageEnd: 1,
      charStart,
      charEnd,
    });
  }

  return sections;
}

async function extractPdf(buffer: Buffer): Promise<{ markdown: string; text: string }> {
  const { PDFParse } = await import("pdf-parse");
  const parser = new PDFParse({ data: buffer });
  try {
    const parsed = await parser.getText();
    const text = normalizeText(parsed.text ?? "");
    return {
      markdown: text,
      text,
    };
  } finally {
    await parser.destroy().catch(() => {});
  }
}

async function extractDocx(buffer: Buffer): Promise<{ markdown: string; text: string }> {
  const mammoth = (await import("mammoth")) as unknown as MammothMarkdownConverter;
  const markdownResult = await mammoth.convertToMarkdown({ buffer });
  const markdown = normalizeText(markdownResult.value ?? "");
  return {
    markdown,
    text: normalizeText(markdown.replace(/^#{1,6}\s+/gm, "")),
  };
}

export async function extractLocalDocument(
  input: LocalDocumentExtractionInput,
): Promise<LocalDocumentExtractionResult> {
  const warnings: string[] = [];
  const type = input.mimeType.toLowerCase();

  let markdown = "";
  let text = "";

  if (type === "text/html") {
    markdown = htmlToMarkdown(input.buffer.toString("utf-8"));
    text = normalizeText(markdown.replace(/^#{1,6}\s+/gm, ""));
  } else if (type === "text/csv") {
    const csv = csvToMarkdown(input.buffer.toString("utf-8"));
    markdown = normalizeText(csv.markdown);
    text = normalizeText(csv.plainText);
  } else if (type === "text/plain" || type === "text/markdown" || type.startsWith("text/")) {
    markdown = normalizeText(input.buffer.toString("utf-8"));
    text = markdown;
  } else if (type === "application/pdf") {
    try {
      const extracted = await extractPdf(input.buffer);
      markdown = extracted.markdown;
      text = extracted.text;
    } catch (error) {
      warnings.push(
        `local pdf extraction unavailable: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  } else if (type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
    try {
      const extracted = await extractDocx(input.buffer);
      markdown = extracted.markdown;
      text = extracted.text;
    } catch (error) {
      warnings.push(
        `local docx extraction unavailable: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  } else {
    throw new Error(`unsupported local mime type: ${input.mimeType}`);
  }

  const normalizedMarkdown = normalizeText(markdown);
  const normalizedText = normalizeText(text || markdown);

  return {
    text: normalizedText,
    markdown: normalizedMarkdown,
    pages: buildPages(normalizedText || normalizedMarkdown),
    sections: deriveSections(normalizedMarkdown || normalizedText),
    warnings,
  };
}

export function resolveDocumentSourceKey(
  input: Pick<DocumentConvertInput, "storageId" | "filename">,
  filename: string,
): string {
  return input.filename?.trim() || filename || input.storageId;
}
